import React from 'react'
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import { ExclamationCircleOutlined, TagsOutlined } from '@ant-design/icons'
import { DeleteOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/es/table'
import {
  EProductStatus,
  EUnit,
  ICreateProductVariation,
  ICreateUpdateVariationProduct,
  IFrontVariation,
  IFrontVariationProduct,
  IProductFormData,
  IProductResponse,
  IProductVariationFormData,
  ISubCategoryCharacteristic,
  IUpdateProduct,
  ICharacteristic
} from 'models/product'
import noProductImage from 'assets/images/no-image-product.png'
import { Uploader } from 'components/ui/Uploader/Uploader'
import { productApi } from 'api/product'
import {
  floatNumbersPattern,
  nonZeroPattern,
  numbersPattern
} from 'shared/utils/patterns'
import { showServerError } from 'shared/utils/showServerError'
import { IMAGES_URL } from 'shared/constants/urls'
import {
  MIN_PRODUCT_IMAGE_SIDE,
  productTags,
  TAX_OPTIONS
} from 'shared/constants/product'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { TextOnePhoto } from 'components/ui/Uploader/TextOnePhoto'
import styles from '../ProductForm.module.scss'
import {
  getCreateVariationSubmitData,
  getEditVariationSubmitData
} from '../utils/getSubmitData'
import { validateProductForm } from '../utils/formValidation'
import {
  fetchSubCategoryCharacteristics,
  extractVariationCharacteristics,
  buildVariationCharacteristics,
  getVariationCharacteristicConflicts
} from '../utils/characteristicsSync'
import {
  extractOriginalProductName,
  createVariationNameWithOriginal,
  isGeneratedVariationName
} from '../utils/variationNameUtils'
import { toVariationProduct } from '../utils/variationUtils'
import { SelectedVariation } from './SelectedVariation'
import { StockFields } from './StockFields'

interface IVariantProductModalFormProps {
  parentFormData: IProductFormData
  data: ICreateUpdateVariationProduct
  sellerId: string
  // id основного товара (есть только в режиме редактирования) — для привязки вариации при создании
  parentProductId?: string
  onSelectSuccess: (
    product: IFrontVariationProduct,
    variationIndex: number,
    updatedCharacteristics?: ICharacteristic[]
  ) => void
  openChooseProductModal: (
    variation: IFrontVariation,
    variationIndex: number
  ) => void
  onClose: () => void
}

export const VariantProductModalForm: React.FC<IVariantProductModalFormProps> =
  React.memo(
    ({
      data,
      parentFormData,
      sellerId,
      parentProductId,
      onSelectSuccess,
      openChooseProductModal,
      onClose
    }) => {
      const { visible, variation, type, variationIndex } = data

      const [products, setProducts] = React.useState<IProductResponse[]>([])
      const [isLoading, setIsLoading] = React.useState(false)
      const [isSubmitting, setIsSubmitting] = React.useState(false)
      const [createUpdateForm] = Form.useForm()

      const isUpdate = type === 'update'
      const linkedStatus = products[0]?.status
      // Бэкенд публикует вариацию только у опубликованного родителя
      const canPublish =
        !!parentProductId &&
        parentFormData?.status === EProductStatus.PUBLISHED &&
        (!linkedStatus ||
          linkedStatus === EProductStatus.DRAFT ||
          linkedStatus === EProductStatus.REJECTED)

      const [deletedPhotos, setDeletedPhotos] = React.useState<string[]>([])
      const [subCategoryCharacteristics, setSubCategoryCharacteristics] =
        React.useState<ISubCategoryCharacteristic[]>([])
      // форму заполняем только после загрузки характеристик подкатегории,
      // иначе список обязательных полей соберётся неполным
      const [isSubCategoryLoaded, setIsSubCategoryLoaded] =
        React.useState(false)
      // характеристики выбранного товара до привязки — по ним ищем расхождения со строкой таблицы
      const [productCharacteristics, setProductCharacteristics] = React.useState<
        ICharacteristic[]
      >([])

      // строку таблицы можно править, пока модалка открыта, — берём её актуальное состояние
      const actualVariation = React.useMemo(
        () =>
          parentFormData?.variations?.find(
            (v, index) => index === variationIndex
          ) ?? variation,
        [parentFormData?.variations, variationIndex, variation]
      )

      const variationCharacteristics = React.useMemo(
        () =>
          actualVariation
            ? extractVariationCharacteristics(actualVariation)
            : [],
        [actualVariation]
      )

      // имена характеристик, которые задаёт таблица вариаций: в форме они только для чтения
      const lockedCharacteristicNames = React.useMemo(
        () =>
          variationCharacteristics.map((item) => item.name.trim().toLowerCase()),
        [variationCharacteristics]
      )

      const characteristics: ICharacteristic[] =
        Form.useWatch('requiredCharacteristics', {
          form: createUpdateForm,
          preserve: true
        }) ?? []

      const productId: string | undefined = Form.useWatch('id', {
        form: createUpdateForm,
        preserve: true
      })

      const photo =
        Form.useWatch('photo', {
          form: createUpdateForm,
          preserve: true
        }) ?? []

      const getProductNameFormParent = () => {
        // Получаем актуальные значения характеристик из родительской формы
        // Ищем актуальную вариацию в массиве variations из parentFormData
        const actualVariation = parentFormData?.variations?.find(
          (v, index) => index === variationIndex
        )

        const actualFirstValue =
          actualVariation?.firstCharacteristicValue ||
          variation?.firstCharacteristicValue
        const actualSecondValue =
          actualVariation?.secondCharacteristicValue ||
          variation?.secondCharacteristicValue

        if (actualFirstValue && actualSecondValue) {
          return `(${actualFirstValue.toLocaleLowerCase()}, ${actualSecondValue.toLocaleLowerCase()})`
        }
        if (actualFirstValue) {
          return `(${actualFirstValue.toLocaleLowerCase()})`
        }
        if (actualSecondValue) {
          return `(${actualSecondValue.toLocaleLowerCase()})`
        }
        return ''
      }

      const getVariationProductName = (existingProductName?: string) => {
        // Если товар уже существует и его название не было сгенерировано автоматически,
        // сохраняем оригинальное название и добавляем характеристики
        if (existingProductName && parentFormData?.name) {
          const isGenerated = isGeneratedVariationName(
            existingProductName,
            parentFormData.name
          )

          if (!isGenerated) {
            // Извлекаем оригинальное название (убираем старые характеристики, если есть)
            const originalName = extractOriginalProductName(existingProductName)

            // Создаем актуальную вариацию для генерации суффикса
            const currentVariation: IFrontVariation = {
              firstCharacteristicValue:
                parentFormData?.variations?.find(
                  (v, index) => index === variationIndex
                )?.firstCharacteristicValue ||
                variation?.firstCharacteristicValue,
              secondCharacteristicValue:
                parentFormData?.variations?.find(
                  (v, index) => index === variationIndex
                )?.secondCharacteristicValue ||
                variation?.secondCharacteristicValue
            }

            return createVariationNameWithOriginal(
              originalName,
              currentVariation
            )
          }
        }

        // Для новых товаров или товаров с автоматически сгенерированными названиями
        // используем стандартную логику
        return parentFormData?.name
          ? `${parentFormData.name} ${getProductNameFormParent()}`
          : undefined
      }

      const getSubCategoryCharacteristics = async (subCategoryId: string) => {
        const characteristics = await fetchSubCategoryCharacteristics(
          subCategoryId
        )
        setSubCategoryCharacteristics(characteristics)
        setIsSubCategoryLoaded(true)
      }

      React.useEffect(() => {
        if (!visible) {
          // Полностью очищаем состояние при закрытии модального окна
          createUpdateForm.resetFields()
          setProducts([])
          setDeletedPhotos([])
          setSubCategoryCharacteristics([])
          setIsSubCategoryLoaded(false)
          setProductCharacteristics([])
          // В упрощенной версии нет дополнительных состояний для очистки
          setIsLoading(false)
          setIsSubmitting(false)
          setIsFormInitialized(false) // Сбрасываем флаг инициализации
        }
      }, [visible])

      // Вариация живёт в подкатегории основного товара — её характеристики и определяют
      // список обязательных полей в модалке
      React.useEffect(() => {
        if (!visible) return

        const subCategoryId = parentFormData?.categories_id?.[1]
        if (subCategoryId) {
          getSubCategoryCharacteristics(subCategoryId)
        } else {
          setIsSubCategoryLoaded(true)
        }
      }, [visible, parentFormData?.categories_id])

      // Состояние для отслеживания инициализации формы
      const [isFormInitialized, setIsFormInitialized] = React.useState(false)

      React.useEffect(() => {
        if (
          variation?.product &&
          isUpdate &&
          visible &&
          isSubCategoryLoaded &&
          !isFormInitialized
        ) {
          const getProductData = async () => {
            try {
              setIsLoading(true)
              const { data } = await productApi.getProduct(
                variation?.product?.id as string
              )
              setProducts([data])

              const ownCharacteristics = (data.characteristic ??
                []) as ICharacteristic[]
              setProductCharacteristics(ownCharacteristics)

              // Инициализируем форму с данными товара-вариации.
              // Пустые габариты предзаполняем значениями основного товара — только в форме,
              // в базу они попадут при подтверждении модалки (кнопка «Выбрать»)
              createUpdateForm.setFields([
                { name: 'id', value: data._id },
                { name: 'seller', value: data.seller_id },
                { name: 'name', value: data.name },
                { name: 'vendor_code', value: data.vendor_code },
                { name: 'price', value: data.price },
                { name: 'tax', value: data.tax },
                { name: 'width', value: data?.dimension?.width ?? parentFormData?.width },
                { name: 'length', value: data?.dimension?.length ?? parentFormData?.length },
                { name: 'height', value: data?.dimension?.height ?? parentFormData?.height },
                { name: 'weight', value: data?.dimension?.weight ?? parentFormData?.weight },
                { name: 'unit', value: data?.dimension?.unit ?? parentFormData?.unit },
                { name: 'in_stock', value: data?.dimension?.in_stock },
                {
                  name: 'on_demand',
                  // dimension в ответе типизирован alias-ом без on_demand — читаем через каст
                  value: !!(
                    data?.dimension as { on_demand?: boolean } | undefined
                  )?.on_demand
                },
                {
                  name: 'auto_deduct_stock',
                  // у вариаций, созданных до появления поля, списание считается включённым
                  value: (
                    data?.dimension as
                      | { auto_deduct_stock?: boolean }
                      | undefined
                  )?.auto_deduct_stock !== false
                },
                {
                  name: 'photo',
                  value: data?.photo?.[0]
                    ? [
                      {
                        uid: data.photo[0],
                        thumbUrl: `${IMAGES_URL}${data.photo[0]}`
                      }
                    ]
                    : undefined
                },
                { name: 'desc', value: data.desc },
                {
                  name: 'requiredCharacteristics',
                  value: buildVariationCharacteristics(
                    ownCharacteristics,
                    variationCharacteristics,
                    subCategoryCharacteristics
                  )
                }
              ])

              // В упрощенной версии работаем только с характеристиками вариации

              // Обрабатываем дополнительные фотографии
              if (data.photo && data.photo.length > 1) {
                setDeletedPhotos(data.photo.filter((_, index) => !!index))
              }

              // Помечаем форму как инициализированную
              setIsFormInitialized(true)
            } catch (e) {
              showServerError(e)
            } finally {
              setIsLoading(false)
            }
          }
          getProductData()
        }
      }, [
        variation,
        visible,
        isUpdate,
        isSubCategoryLoaded,
        isFormInitialized
      ])

      React.useEffect(() => {
        if (
          !isUpdate &&
          visible &&
          variation &&
          !variation?.product &&
          isSubCategoryLoaded &&
          !isFormInitialized
        ) {
          // Новый товар наследует характеристики основного: расхождений с таблицей
          // тут быть не может, поэтому productCharacteristics остаются пустыми
          const parentCharacteristics: ICharacteristic[] = [
            ...(parentFormData?.requiredCharacteristics || []),
            ...(parentFormData?.optionalCharacteristics || []),
            ...(parentFormData?.characteristics || [])
          ]

          // Инициализируем форму с данными из родительского товара
          createUpdateForm.setFields([
            { name: 'id', value: parentFormData?.id },
            { name: 'seller', value: sellerId },
            {
              name: 'name',
              value: getVariationProductName() || ''
            },
            { name: 'vendor_code', value: parentFormData?.vendor_code },
            { name: 'price', value: parentFormData?.price },
            { name: 'tax', value: parentFormData?.tax },
            { name: 'width', value: parentFormData?.width },
            { name: 'length', value: parentFormData?.length },
            { name: 'height', value: parentFormData?.height },
            { name: 'weight', value: parentFormData?.weight },
            { name: 'unit', value: parentFormData?.unit },
            { name: 'in_stock', value: parentFormData?.in_stock },
            { name: 'on_demand', value: !!parentFormData?.on_demand },
            {
              name: 'auto_deduct_stock',
              value: parentFormData?.auto_deduct_stock !== false
            },
            {
              name: 'photo',
              value: parentFormData?.photo?.[0]
                ? [parentFormData?.photo?.[0]]
                : []
            },
            { name: 'desc', value: parentFormData?.desc },
            {
              name: 'requiredCharacteristics',
              value: buildVariationCharacteristics(
                parentCharacteristics,
                variationCharacteristics,
                subCategoryCharacteristics
              )
            }
          ])

          // В упрощенной версии нет дополнительных характеристик для очистки

          // Помечаем форму как инициализированную
          setIsFormInitialized(true)
        }
      }, [
        visible,
        variation,
        isUpdate,
        variation?.product,
        isSubCategoryLoaded,
        isFormInitialized,
        parentFormData,
        sellerId
      ])

      // Обновляем название товара и характеристики при изменении значений в родительской форме
      React.useEffect(() => {
        if (visible && isFormInitialized && parentFormData?.name) {
          const currentName = createUpdateForm.getFieldValue('name')
          const newName =
            getVariationProductName(currentName) || currentName || ''

          // Обновляем название только если оно изменилось
          if (currentName !== newName) {
            createUpdateForm.setFieldValue('name', newName)
          }

          // Подтягиваем значения из строки таблицы, не трогая остальные характеристики:
          // их продавец мог уже заполнить руками прямо в модалке
          const currentCharacteristics: ICharacteristic[] =
            createUpdateForm.getFieldValue('requiredCharacteristics') || []

          let hasChanges = false
          const updatedCharacteristics = currentCharacteristics.map((item) => {
            const fromVariation = variationCharacteristics.find(
              (locked) =>
                locked.name.trim().toLowerCase() ===
                item.name?.trim().toLowerCase()
            )

            if (!fromVariation || fromVariation.value === item.value) {
              return item
            }

            hasChanges = true
            return { ...item, value: fromVariation.value }
          })

          if (hasChanges) {
            createUpdateForm.setFieldValue(
              'requiredCharacteristics',
              updatedCharacteristics
            )
          }
        }
      }, [
        visible,
        isFormInitialized,
        variationCharacteristics,
        parentFormData?.name,
        variationIndex
      ])

      const submitVariation = async (
        data: IProductVariationFormData,
        status: EProductStatus.DRAFT | EProductStatus.PUBLISHED | undefined
      ) => {
        try {
          setIsSubmitting(true)
          const normalizedData = isUpdate
            ? getEditVariationSubmitData(
              data,
              productId as string,
              deletedPhotos,
              status === EProductStatus.PUBLISHED ? status : undefined
            )
            : getCreateVariationSubmitData(
              data,
              sellerId,
              parentProductId,
              status ?? EProductStatus.DRAFT
            )

          if (isUpdate) {
            const response = await productApi.updateProduct(
              normalizedData as IUpdateProduct
            )

            // Формируем правильное название с актуальными характеристиками
            const currentName = createUpdateForm.getFieldValue('name')
            const updatedName =
              getVariationProductName(currentName) || currentName || ''
            onSelectSuccess(
              toVariationProduct(response.data, updatedName),
              variationIndex as number,
              response.data.characteristic
            )
          } else {
            const response = await productApi.createProductVariation(
              normalizedData as ICreateProductVariation
            )

            // Формируем правильное название с актуальными характеристиками
            const currentName = createUpdateForm.getFieldValue('name')
            const updatedName =
              getVariationProductName(currentName) || currentName || ''
            onSelectSuccess(
              toVariationProduct(response.data, updatedName),
              variationIndex as number,
              response.data.characteristic
            )
          }
        } catch (e: any) {
          if (!e.errorFields) {
            showServerError(e)
          }
        } finally {
          setIsSubmitting(false)
        }
      }

      const onSubmit = async (
        status: EProductStatus.DRAFT | EProductStatus.PUBLISHED | undefined
      ) => {
        const data = await validateProductForm<IProductVariationFormData>(
          createUpdateForm,
          status ?? linkedStatus
        )
        if (!data) return

        const conflicts = getVariationCharacteristicConflicts(
          productCharacteristics,
          variationCharacteristics
        )

        if (!conflicts.length) {
          submitVariation(data, status)
          return
        }

        Modal.confirm({
          centered: true,
          icon: <ExclamationCircleOutlined />,
          title: 'Характеристики товара будут изменены',
          content: (
            <>
              <Typography.Paragraph>
                У выбранного товара свои значения характеристик вариации. При
                связывании они заменятся значениями из таблицы вариаций:
              </Typography.Paragraph>
              {conflicts.map((conflict) => (
                <Typography.Paragraph
                  key={conflict.name}
                  style={{ marginBottom: 4 }}
                >
                  <b>{conflict.name}</b>: «{conflict.productValue}» → «
                  {conflict.variationValue}»
                </Typography.Paragraph>
              ))}
            </>
          ),
          okText: 'Заменить и связать',
          cancelText: 'Отмена',
          onOk: () => submitVariation(data, status)
        })
      }

      const columns: ColumnsType<IProductResponse> = React.useMemo(
        () => [
          {
            dataIndex: 'vendor_code',
            width: 101,
            align: 'center'
          },
          {
            dataIndex: 'photo',
            width: 70,
            align: 'center',
            render: (_, { photo }) => {
              return photo && !!photo.length ? (
                <div
                  style={{
                    width: 62,
                    height: 56,
                    backgroundImage: `url(${IMAGES_URL}${photo[0]})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                />
              ) : (
                <img
                  src={noProductImage}
                  alt='Изображение товара'
                  width={62}
                  height={56}
                />
              )
            }
          },
          {
            dataIndex: 'name',
            width: 282
          },
          {
            dataIndex: 'category_id',
            width: 282,
            render: (_, { category_id, subCategory_id }) => {
              if (!category_id || !subCategory_id) {
                return (
                  <Typography.Text style={{ opacity: 0.5 }}>
                    Не задана
                  </Typography.Text>
                )
              }
              return (
                <>
                  <Typography.Text>{category_id.name + ' > '}</Typography.Text>
                  <Typography.Text>{subCategory_id.name}</Typography.Text>
                </>
              )
            }
          },
          {
            dataIndex: 'price',
            width: 101,
            align: 'center',
            render: (_, { price }) => {
              return (
                <Typography.Text style={{ opacity: price ? 1 : 0.5 }}>
                  {price ? getNormalizedPrice(price) : 'Не задана'}
                </Typography.Text>
              )
            }
          },
          {
            dataIndex: 'status',
            width: 136,
            align: 'center',
            render: (_, { status }) => {
              return (
                <Tag color={productTags[status].color}>
                  {productTags[status].label}
                </Tag>
              )
            }
          },
          {
            title: '',
            dataIndex: '',
            width: 58,
            align: 'center',
            render: () => {
              return (
                <Button
                  htmlType='button'
                  type='primary'
                  size='middle'
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    openChooseProductModal(
                      variation as IFrontVariation,
                      variationIndex as number
                    )
                  }
                />
              )
            }
          }
        ],
        [products]
      )

      if (isLoading) {
        return null
      }

      return (
        <Modal
          centered
          open={visible}
          footer={null}
          closable={false}
          onCancel={onClose}
          width={1100}
        >
          <SelectedVariation
            title={isUpdate ? 'Товар для' : 'Создание товара для'}
            variation={variation as IFrontVariation}
            onClose={onClose}
          />
          {isUpdate && (
            <Table
              scroll={{
                x: 700
              }}
              size='small'
              columns={columns}
              showHeader={false}
              bordered={true}
              dataSource={products}
              rowKey={(el) => el._id}
              pagination={false}
              style={{ marginTop: 15, marginBottom: 30 }}
            />
          )}
          <Form
            form={createUpdateForm}
            name='createUpdateForm'
            size='middle'
            style={{ marginTop: isUpdate ? 20 : 17 }}
          >
            <div className={styles.createUpdateVariantProductFormContent}>
              <div
                className={styles.createUpdateVariantProductFormContentUploader}
              >
                <Uploader
                  name='photo'
                  type='variation'
                  minSize={MIN_PRODUCT_IMAGE_SIDE}
                  disabled={!!photo.length}
                  rules={[]}
                  onRemoveClick={(file) => {
                    if (!file.originFileObj) {
                      setDeletedPhotos((prev) => [...prev, file?.uid ?? ''])
                    }
                  }}
                />
                <TextOnePhoto />
              </div>
              <div
                className={styles.createUpdateVariantProductFormContentFields}
              >
                <Form.Item
                  name='name'
                  label='Название товара'
                  rules={[
                    {
                      required: true,
                      message: 'Заполните название товара'
                    },
                    {
                      whitespace: true,
                      message: 'Заполните название товара'
                    }
                  ]}
                  hasFeedback
                >
                  <Input placeholder='Ваше название' />
                </Form.Item>
                <Form.Item
                  name='vendor_code'
                  label='Артикул продавца'
                  rules={[
                    {
                      required: true,
                      message: 'Заполните артикул продавца'
                    },
                    {
                      whitespace: true,
                      message: 'Заполните артикул продавца'
                    }
                  ]}
                  hasFeedback
                >
                  <Input placeholder='Ваш артикул продавца' />
                </Form.Item>
                <div className={styles.flexBig} style={{ gap: 10 }}>
                  <Form.Item
                    name='price'
                    label='Стоимость'
                    rules={[
                      {
                        required: true,
                        message: 'Заполните стоимость товара'
                      },
                      {
                        pattern: floatNumbersPattern,
                        message: 'Заполните стоимость товара корректно'
                      },
                      {
                        pattern: nonZeroPattern,
                        message: 'Стоимость должна быть больше нуля'
                      }
                    ]}
                    className={styles.inputFlex}
                    hasFeedback
                  >
                    <Input placeholder='Цена в рублях для покупателя' />
                  </Form.Item>
                  <Form.Item
                    name='tax'
                    rules={[
                      {
                        required: true,
                        message: 'Выберите размер налога'
                      }
                    ]}
                    style={{ width: 238, marginBottom: 0 }}
                    hasFeedback
                  >
                    <Select
                      placeholder='Размер налога'
                      options={TAX_OPTIONS}
                    />
                  </Form.Item>
                </div>
                <Form.Item
                  name='width'
                  label='Ширина'
                  rules={[
                    {
                      required: true,
                      message: 'Заполните ширину упакованного товара'
                    },
                    {
                      pattern: numbersPattern,
                      message: 'Заполните ширину упакованного товара корректно'
                    },
                    {
                      pattern: nonZeroPattern,
                      message: 'Ширина должна быть больше нуля'
                    }
                  ]}
                  hasFeedback
                >
                  <Input placeholder='Ширина упакованного товара в сантиметрах' />
                </Form.Item>
                <Form.Item
                  name='length'
                  label='Длина'
                  rules={[
                    {
                      required: true,
                      message: 'Заполните длину упакованного товара'
                    },
                    {
                      pattern: numbersPattern,
                      message: 'Заполните длину упакованного товара корректно'
                    },
                    {
                      pattern: nonZeroPattern,
                      message: 'Длина должна быть больше нуля'
                    }
                  ]}
                  hasFeedback
                >
                  <Input placeholder='Длина упакованного товара в сантиметрах' />
                </Form.Item>
                <Form.Item
                  name='height'
                  label='Высота'
                  rules={[
                    {
                      required: true,
                      message: 'Заполните высоту упакованного товара'
                    },
                    {
                      pattern: numbersPattern,
                      message: 'Заполните высоту упакованного товара корректно'
                    },
                    {
                      pattern: nonZeroPattern,
                      message: 'Высота должна быть больше нуля'
                    }
                  ]}
                  hasFeedback
                >
                  <Input placeholder='Высота упакованного товара в сантиметрах' />
                </Form.Item>
                <div className={styles.weightRow} style={{ gap: 10 }}>
                  <Form.Item
                    name='weight'
                    label='Вес'
                    rules={[
                      {
                        required: true,
                        message: 'Заполните вес товара'
                      },
                      {
                        pattern: floatNumbersPattern,
                        message: 'Заполните вес товара корректно'
                      },
                      {
                        pattern: nonZeroPattern,
                        message: 'Вес должен быть больше нуля'
                      }
                    ]}
                    className={styles.inputFlex}
                    hasFeedback
                  >
                    <Input placeholder='Общий вес товара' />
                  </Form.Item>
                  <Form.Item
                    name='unit'
                    label='Единицы измерения'
                    rules={[
                      {
                        required: true,
                        message: 'Выберите единицы измерения'
                      }
                    ]}
                    className={styles.unitField}
                    hasFeedback
                  >
                    <Select
                      placeholder='Единицы измерения'
                      options={[
                        { value: EUnit.GR, label: 'Граммы' },
                        { value: EUnit.KG, label: 'Килограммы' }
                      ]}
                    />
                  </Form.Item>
                </div>
                <StockFields form={createUpdateForm} />
                <Typography.Title
                  level={4}
                  className={styles.sectionSubTitle}
                  style={{ marginBottom: 16 }}
                >
                  Описание:
                </Typography.Title>
                <Form.Item name='desc' hasFeedback>
                  <Input.TextArea placeholder='Описание товара' rows={4} />
                </Form.Item>
                {characteristics.length > 0 && (
                  <>
                    <Typography.Title
                      level={4}
                      className={styles.sectionSubTitle}
                      style={{ marginBottom: 18, marginTop: 24 }}
                    >
                        Характеристики:
                    </Typography.Title>

                    <div className={styles.characteristicsWrapper}>
                      {characteristics.map((characteristic, index) => {
                        const name = characteristic?.name ?? ''
                        const subCategoryChar = subCategoryCharacteristics.find(
                          (subChar) =>
                            subChar.name.toLowerCase() === name.toLowerCase()
                        )
                        // значение из таблицы вариаций правится только в самой таблице
                        const isLocked = lockedCharacteristicNames.includes(
                          name.trim().toLowerCase()
                        )
                        const isRequired =
                          isLocked || !!subCategoryChar?.required
                        const isSelect =
                          !!subCategoryChar?.hints?.length &&
                          !subCategoryChar.isAnyValue

                        const disabledStyle = {
                          color: 'rgba(0, 0, 0, 0.45)',
                          backgroundColor: '#f5f5f5'
                        }

                        const valueField = (
                          <Form.Item
                            name={['requiredCharacteristics', index, 'value']}
                            className={styles.characteristicRowField}
                            rules={
                              isRequired
                                ? [
                                  {
                                    required: true,
                                    message: `Заполните ${name.toLowerCase()}`
                                  }
                                ]
                                : undefined
                            }
                            hasFeedback
                          >
                            {isSelect ? (
                              <Select
                                size='middle'
                                showSearch
                                placeholder={`Выберите ${name.toLowerCase()}`}
                                options={subCategoryChar.hints.map(
                                  (hint) => ({
                                    value: hint,
                                    label: hint
                                  })
                                )}
                                disabled={isLocked}
                                style={isLocked ? disabledStyle : undefined}
                              />
                            ) : (
                              <Input
                                size='middle'
                                placeholder={`Введите ${name.toLowerCase()}`}
                                disabled={isLocked}
                                style={isLocked ? disabledStyle : undefined}
                              />
                            )}
                          </Form.Item>
                        )

                        return (
                          <div
                            key={`variation-${index}`}
                            className={styles.characteristicRow}
                          >
                            <TagsOutlined className={styles.characteristicRowIcon} />
                            <Form.Item
                              name={['requiredCharacteristics', index, 'name']}
                              className={styles.characteristicRowField}
                              hasFeedback
                            >
                              <Input
                                size='middle'
                                placeholder='Название характеристики'
                                disabled
                                style={disabledStyle}
                                suffix={
                                  isRequired ? (
                                    <span style={{ color: '#ff4d4f' }}>*</span>
                                  ) : undefined
                                }
                              />
                            </Form.Item>
                            {isLocked ? (
                              <Tooltip title='Задаётся в таблице вариаций'>
                                <div className={styles.characteristicRowField}>
                                  {valueField}
                                </div>
                              </Tooltip>
                            ) : (
                              valueField
                            )}
                            <div className={styles.characteristicRowSpacer} />
                          </div>
                        )
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className={styles.chooseVariationProductFooter}>
              <Button
                type='default'
                size='middle'
                onClick={() =>
                  openChooseProductModal(
                    variation as IFrontVariation,
                    variationIndex as number
                  )
                }
              >
                {isUpdate
                  ? 'Выбрать другой товар'
                  : 'Выбрать уже созданный товар'}
              </Button>
              <Button
                type={canPublish ? 'default' : 'primary'}
                size='middle'
                loading={isSubmitting}
                onClick={() =>
                  onSubmit(isUpdate ? undefined : EProductStatus.DRAFT)
                }
              >
                {isUpdate ? 'Сохранить и связать' : 'Сохранить черновиком'}
              </Button>
              {canPublish && (
                <Button
                  type='primary'
                  size='middle'
                  loading={isSubmitting}
                  onClick={() => onSubmit(EProductStatus.PUBLISHED)}
                >
                  {isUpdate ? 'Опубликовать и связать' : 'Опубликовать'}
                </Button>
              )}
            </div>
          </Form>
        </Modal>
      )
    }
  )
